import type { ExtraPurchaseFieldBinding } from './extraPurchaseFieldBindings'

export const STUDENT_IDENTIFICATION_FIELD_KEYS = [
  'studentEmail',
  'studentPhone',
] as const

export const PARENT_IDENTIFICATION_FIELD_KEYS = [
  'parentEmail',
  'parentPhone',
] as const

export const STUDENT_IDENTIFICATION_TOOLTIP =
  'Идентификационное поле ученика. По нему в поп-апе записи проверяется, есть ли уже ученик или лид CRM с такими данными.'

export const PARENT_IDENTIFICATION_TOOLTIP =
  'Идентификационное поле родителя. По нему в поп-апе записи проверяется, есть ли уже родитель в системе.'

export type ExtraFieldRule = {
  key: string
  state: boolean
  tooltip: string
}

export const isStudentIdentificationField = (fieldKey: string) =>
  (STUDENT_IDENTIFICATION_FIELD_KEYS as readonly string[]).includes(fieldKey)

export const isParentIdentificationField = (fieldKey: string) =>
  (PARENT_IDENTIFICATION_FIELD_KEYS as readonly string[]).includes(fieldKey)

export const isIdentificationField = (fieldKey: string) =>
  isStudentIdentificationField(fieldKey) ||
  isParentIdentificationField(fieldKey)

export const referencesStudentProfile = (
  binding?: ExtraPurchaseFieldBinding | null,
) => !!binding?.children_field_id

export const referencesParentProfile = (
  binding?: ExtraPurchaseFieldBinding | null,
) => !!binding?.parent_field_id

export const referencesAnyProfile = (
  binding?: ExtraPurchaseFieldBinding | null,
) => referencesStudentProfile(binding) || referencesParentProfile(binding)

export const isStudentProfileDependentField = (
  fieldKey: string,
  binding?: ExtraPurchaseFieldBinding | null,
) =>
  !isIdentificationField(fieldKey) &&
  (referencesStudentProfile(binding) || referencesParentProfile(binding))

export const isParentProfileDependentField = (
  fieldKey: string,
  binding?: ExtraPurchaseFieldBinding | null,
) =>
  !isIdentificationField(fieldKey) && referencesParentProfile(binding)

export const isFieldEffectivelyEnabled = (
  fieldKey: string,
  extraFields: string[],
  fieldsRules: ExtraFieldRule[] = [],
) => {
  const rule = fieldsRules.find((item) => item.key === fieldKey)
  if (rule) return rule.state

  return extraFields.includes(fieldKey)
}

export const hasActiveStudentIdentification = (
  extraFields: string[],
  fieldsRules: ExtraFieldRule[] = [],
) =>
  STUDENT_IDENTIFICATION_FIELD_KEYS.some((fieldKey) =>
    isFieldEffectivelyEnabled(fieldKey, extraFields, fieldsRules),
  )

export const hasActiveParentIdentification = (
  extraFields: string[],
  fieldsRules: ExtraFieldRule[] = [],
) =>
  PARENT_IDENTIFICATION_FIELD_KEYS.some((fieldKey) =>
    isFieldEffectivelyEnabled(fieldKey, extraFields, fieldsRules),
  )

export const isFieldBlockedByIdentification = (
  fieldKey: string,
  extraFields: string[],
  fieldsRules: ExtraFieldRule[] = [],
  binding?: ExtraPurchaseFieldBinding | null,
) => {
  if (isIdentificationField(fieldKey)) return false

  if (
    !hasActiveStudentIdentification(extraFields, fieldsRules) &&
    isStudentProfileDependentField(fieldKey, binding)
  ) {
    return true
  }

  if (
    !hasActiveParentIdentification(extraFields, fieldsRules) &&
    isParentProfileDependentField(fieldKey, binding)
  ) {
    return true
  }

  return false
}

export const stripInactiveGroupDependents = (
  fields: string[],
  bindingsByKey: Map<string, ExtraPurchaseFieldBinding>,
  fieldsRules: ExtraFieldRule[] = [],
) =>
  fields.filter(
    (fieldKey) =>
      !isFieldBlockedByIdentification(
        fieldKey,
        fields,
        fieldsRules,
        bindingsByKey.get(fieldKey),
      ),
  )

export const getEffectiveExtraFields = (
  fields: string[],
  bindingsByKey: Map<string, ExtraPurchaseFieldBinding>,
  fieldsRules: ExtraFieldRule[] = [],
) => stripInactiveGroupDependents(fields, bindingsByKey, fieldsRules)

export type FieldProfileDependencyRequirements = {
  needsStudentIdentification: boolean
  needsParentIdentification: boolean
}

export const getFieldProfileDependencyRequirements = (
  fieldKey: string,
  binding?: ExtraPurchaseFieldBinding | null,
): FieldProfileDependencyRequirements => ({
  needsStudentIdentification: isStudentProfileDependentField(fieldKey, binding),
  needsParentIdentification: isParentProfileDependentField(fieldKey, binding),
})

const formatIdentificationRequirements = (
  requirements: FieldProfileDependencyRequirements,
) => {
  const parts: string[] = []

  if (requirements.needsStudentIdentification) {
    parts.push('почта или телефон ученика')
  }

  if (requirements.needsParentIdentification) {
    parts.push('почта или телефон родителя')
  }

  return parts
}

export const getDependentFieldTooltip = (
  fieldKey: string,
  binding?: ExtraPurchaseFieldBinding | null,
) => {
  const requirements = getFieldProfileDependencyRequirements(fieldKey, binding)
  const parts = formatIdentificationRequirements(requirements)

  if (!parts.length) return undefined

  if (parts.length === 1) {
    return `Зависимое поле. Доступно только при включённой ${parts[0]}.`
  }

  return `Зависимое поле. Доступно при включённых ${parts[0]} и ${parts[1]}.`
}

export const getDependentFieldBlockedTooltip = (
  fieldKey: string,
  binding: ExtraPurchaseFieldBinding | null | undefined,
  extraFields: string[],
  fieldsRules: ExtraFieldRule[] = [],
) => {
  const requirements = getFieldProfileDependencyRequirements(fieldKey, binding)
  const missing: string[] = []

  if (
    requirements.needsStudentIdentification &&
    !hasActiveStudentIdentification(extraFields, fieldsRules)
  ) {
    missing.push('почту или телефон ученика')
  }

  if (
    requirements.needsParentIdentification &&
    !hasActiveParentIdentification(extraFields, fieldsRules)
  ) {
    missing.push('почту или телефон родителя')
  }

  if (!missing.length) return undefined

  if (missing.length === 1) {
    return `Поле заблокировано: включите ${missing[0]}, чтобы им можно было управлять.`
  }

  return `Поле заблокировано: включите ${missing[0]} и ${missing[1]}, чтобы им можно было управлять.`
}

export const getIdentificationFieldTooltip = (fieldKey: string) => {
  if (isStudentIdentificationField(fieldKey)) {
    return STUDENT_IDENTIFICATION_TOOLTIP
  }

  if (isParentIdentificationField(fieldKey)) {
    return PARENT_IDENTIFICATION_TOOLTIP
  }

  return undefined
}

export const isProfileDependentField = (
  fieldKey: string,
  binding?: ExtraPurchaseFieldBinding | null,
) =>
  isStudentProfileDependentField(fieldKey, binding) ||
  isParentProfileDependentField(fieldKey, binding)
